
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Skeleton } from '@/components/ui/skeleton';
import ReactMarkdown from 'react-markdown';
import { useToast } from '@/components/ui/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { LoginPromptDialog } from '@/components/startup/LoginPromptDialog';
import { Badge } from '@/components/ui/badge';
import { User } from 'lucide-react';

const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [showLoginPrompt, setShowLoginPrompt] = React.useState(!user);
  
  const { data: post, isLoading, error } = useQuery({
    queryKey: ['blog-post', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('blog_posts')
        .select(`
          *,
          user:user_id (
            username,
            avatar_url,
            is_pro
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error fetching blog post:', error);
        throw error;
      }
      
      if (!data) {
        throw new Error('Blog post not found');
      }
      
      return data;
    },
    retry: 1,
    meta: {
      errorMessage: "Unable to load blog post. It may have been deleted or you may not have permission to view it."
    }
  });

  React.useEffect(() => {
    if (error) {
      console.error('Blog post fetch error:', error);
      toast({
        title: "Error",
        description: "Unable to load blog post. It may have been deleted or you may not have permission to view it.",
        variant: "destructive"
      });
      navigate('/blog');
    }
  }, [error, toast, navigate]);

  const customRenderers = {
    img: ({ src, alt }: { src: string; alt?: string }) => {
      const widthMatch = alt?.match(/\|width=(\d+)/);
      const width = widthMatch ? parseInt(widthMatch[1]) : undefined;
      const cleanAlt = alt?.replace(/\|width=\d+/, '').trim();
      
      return (
        <img
          src={src}
          alt={cleanAlt}
          style={width ? { width: `${width}px` } : undefined}
          className="rounded-lg"
        />
      );
    }
  };

  if (isLoading) {
    return (
      <div className="container py-8 max-w-4xl mx-auto space-y-6">
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-[300px] w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="container py-8 text-center">
        <h1 className="text-2xl text-gray-400">Blog post not found</h1>
      </div>
    );
  }

  return (
    <>
      {showLoginPrompt && (
        <LoginPromptDialog 
          isOpen={showLoginPrompt}
          onClose={() => setShowLoginPrompt(false)}
        />
      )}
      
      <div className="container py-8 max-w-4xl mx-auto space-y-6">
        <h1 className="text-4xl font-bold text-white">{post.title}</h1>
        
        <div className="flex items-center gap-3 text-sm text-gray-400">
          <div className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {post.user?.username ? (
              <div className="flex items-center gap-2">
                <span>{post.user.username}</span>
                {post.user.is_pro && (
                  <Badge variant="pioneer" className="ml-2">
                    Pro Member
                  </Badge>
                )}
              </div>
            ) : (
              <span>Anonymous</span>
            )}
          </div>
          <span>•</span>
          <time>{new Date(post.created_at).toLocaleDateString()}</time>
        </div>

        <div className="prose prose-invert max-w-none">
          <ReactMarkdown components={customRenderers}>{post.content}</ReactMarkdown>
        </div>
      </div>
    </>
  );
};

export default BlogPost;
